import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchCiternes } from '@/store/thunks/citerneThunks';
import { fetchTiers } from '@/store/thunks/tierThunks';
import { createEntree, updateEntree } from '@/store/thunks/movementThunks';
import type { MovementRecord } from '@/store/slices/movementSlice';
import type { Citerne } from '@/types';
import type { Tier } from '@/types';
import { Input, Button, Select, SearchSelect, Textarea } from '@/components/ui';

const schema = Yup.object({
  citerneId: Yup.string().required('Citerne obligatoire'),
  tierId: Yup.string().required('Fournisseur obligatoire'),
  dateAchat: Yup.string().required('Date obligatoire'),
  prixAchat: Yup.number().required('Prix obligatoire').min(0),
  quantite: Yup.number().required('Quantité obligatoire').min(0),
  type: Yup.string().oneOf(['RENDUE', 'DEPARTE']).required('Type obligatoire'),
  remarque: Yup.string(),
});

interface EntreeFormProps {
  entree?: MovementRecord | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EntreeForm({ entree, type, onClose, onSuccess }: EntreeFormProps) {
  const dispatch = useAppDispatch();
  const citernes = useAppSelector((s: RootState) => s.citerne.citernes);
  const tiers = useAppSelector((s: RootState) => s.tier.tiers);

  useEffect(() => {
    dispatch(fetchCiternes());
    dispatch(fetchTiers());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      citerneId: entree?.citerne ? String((entree.citerne as { id?: number }).id) : '',
      tierId: entree?.tier ? String((entree.tier as { id?: number }).id) : '',
      dateAchat: entree?.dateAchat ? (entree.dateAchat as string).toString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      prixAchat: entree?.prixAchat ?? '',
      quantite: entree?.quantite ?? '',
      type: (entree?.type as string) ?? 'RENDUE',
      remarque: (entree?.remarque as string) ?? '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const citerne = citernes.find((c: Citerne) => String(c.id) === values.citerneId);
      const tier = tiers.find((t: Tier) => String(t.id) === values.tierId);
      if (!citerne || !tier) return;
      const payload: MovementRecord = {
        citerne: { id: citerne.id, code: citerne.code },
        tier: { id: tier.id, code: tier.code ?? tier.nom },
        dateAchat: values.dateAchat,
        prixAchat: Number(values.prixAchat),
        quantite: Number(values.quantite),
        type: values.type,
        remarque: values.remarque || undefined,
      };
      if (type === 'update' && entree?.id) {
        const res = await dispatch(updateEntree({ ...payload, id: entree.id }));
        if (updateEntree.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createEntree(payload));
        if (createEntree.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const citerneOptions = citernes.map((c: Citerne) => ({ value: String(c.id), label: c.code ?? String(c.id) }));
  const tierOptions = tiers.map((t: Tier) => ({ value: String(t.id), label: t.nom ?? t.code ?? String(t.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <div className="flex gap-4">
        <SearchSelect label="Citerne" options={citerneOptions} value={formik.values.citerneId} onChange={(v) => formik.setFieldValue('citerneId', v)} onBlur={() => formik.setFieldTouched('citerneId', true)} error={formik.touched.citerneId && formik.errors.citerneId ? String(formik.errors.citerneId) : undefined} searchPlaceholder="Rechercher une citerne…" />
        <SearchSelect label="Fournisseur" options={tierOptions} value={formik.values.tierId} onChange={(v) => formik.setFieldValue('tierId', v)} onBlur={() => formik.setFieldTouched('tierId', true)} error={formik.touched.tierId && formik.errors.tierId ? String(formik.errors.tierId) : undefined} searchPlaceholder="Rechercher un fournisseur…" />
      </div>
      <div className="flex gap-4">
        <Input label="Date achat" type="date" id="dateAchat" {...formik.getFieldProps('dateAchat')} error={formik.touched.dateAchat && formik.errors.dateAchat ? String(formik.errors.dateAchat) : undefined} />
        <Input label="Prix" type="number" id="prixAchat" {...formik.getFieldProps('prixAchat')} error={formik.touched.prixAchat && formik.errors.prixAchat ? String(formik.errors.prixAchat) : undefined} />
        <Input label="Quantité" type="number" id="quantite" {...formik.getFieldProps('quantite')} error={formik.touched.quantite && formik.errors.quantite ? String(formik.errors.quantite) : undefined} />
      </div>
      <div className="flex gap-4">
        <Select label="Type" options={[{ value: 'RENDUE', label: 'RENDUE' }, { value: 'DEPARTE', label: 'DEPARTE' }]} value={formik.values.type} onChange={(e) => formik.setFieldValue('type', e.target.value)} />
      </div>
      <Textarea label="Remarque" id="remarque" rows={3} {...formik.getFieldProps('remarque')} />
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{type === 'update' ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
